import { useEffect, useRef, useState } from "react";
import type { DragEvent, ChangeEvent } from "react";
import UploadIcon from "@/components/SVGComponents/UploadIcon";
import ImagePlaceholderIcon from "@/components/SVGComponents/ImagePlaceholderIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import Button from "@/components/Button/Button";
import ErrorText from "@/components/ErrorText/ErrorText";
import toast from "@/lib/toast";
import { combineClasses } from "@/lib/utils";
import styles from "./EventImageUpload.module.scss";

export interface EventImageUploadProps {
  file: File | null;
  onChange: (file: File | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string | undefined;
  label?: string;
  isRequired?: boolean;
  className?: string;
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileExtension = (filename: string): string => {
  const ext = filename.split(".").pop()?.toUpperCase() ?? "IMG";
  return ext.length <= 4 ? ext : "IMG";
};

const EventImageUpload = ({
  file,
  onChange,
  onBlur,
  disabled = false,
  error,
  label = "Event image",
  isRequired = true,
  className,
}: EventImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Generate and safely cleanup the preview object URL
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  const validateAndSetFile = (pickedFile: File | null) => {
    if (!pickedFile) {
      onChange(null);
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(pickedFile.type.toLowerCase())) {
      toast.error("Please upload a valid image file (JPG, PNG, or WEBP)");
      return;
    }

    if (pickedFile.size > MAX_FILE_SIZE_BYTES) {
      toast.error("Image file size should not exceed 5MB");
      return;
    }

    onChange(pickedFile);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    validateAndSetFile(selected);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only turn off if moving outside the dropzone boundary
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFile = e.dataTransfer.files?.[0] ?? null;
    validateAndSetFile(droppedFile);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    onBlur?.();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleOpenFileDialog = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  return (
    <div className={combineClasses(styles.container, className)}>
      {label && (
        <label className={styles.label}>
          {label} {isRequired && <span className={styles.requiredMark}>*</span>}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/jpg"
        disabled={disabled}
        className={styles.hiddenInput}
        onChange={handleInputChange}
      />

      {file ? (
        // ================= SELECTED STATE (PREVIEW & DETAILS) =================
        <div
          className={combineClasses(
            styles.selectedCard,
            Boolean(error) && styles.cardError,
            disabled && styles.disabled,
          )}
        >
          <div className={styles.previewWrapper}>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Event preview"
                className={styles.previewImage}
              />
            ) : (
              <div className={styles.previewFallback}>
                <ImagePlaceholderIcon className={styles.fallbackIcon} />
              </div>
            )}
          </div>

          <div className={styles.fileDetails}>
            <div className={styles.nameRow}>
              <span className={styles.fileName} title={file.name}>
                {file.name}
              </span>
              <span className={styles.formatBadge}>
                {getFileExtension(file.name)}
              </span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
              <span className={styles.readyBadge}>• Ready to upload</span>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <Button
              type="button"
              outlined
              disabled={disabled}
              className={styles.changeButton}
              onClick={handleOpenFileDialog}
            >
              Change
            </Button>
            <button
              type="button"
              disabled={disabled}
              className={styles.removeButton}
              onClick={handleRemove}
              title="Remove image"
              aria-label="Remove image"
            >
              <CrossIcon withBg={false} width={14} height={14} stroke="#d32f2f" strokeWidth={2} />
            </button>
          </div>
        </div>
      ) : (
        // ================= EMPTY STATE (DROPZONE) =================
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          className={combineClasses(
            styles.dropzone,
            isDragging && styles.dragging,
            Boolean(error) && styles.dropzoneError,
            disabled && styles.disabled,
          )}
          onClick={handleOpenFileDialog}
          onBlur={onBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleOpenFileDialog();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={styles.iconCircle}>
            <UploadIcon className={styles.uploadIcon} />
          </div>

          <div className={styles.textColumn}>
            <div className={styles.primaryPrompt}>
              <span className={styles.highlightText}>Click to upload</span>
            </div>
            <div className={styles.helperText}>
              PNG, JPG, or WEBP (Max 5MB · Recommended 16:9)
            </div>
          </div>

          <Button
            type="button"
            outlined
            disabled={disabled}
            className={styles.browseButton}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenFileDialog();
            }}
          >
            Browse
          </Button>
        </div>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
};

export default EventImageUpload;
